import { LlmModelName } from 'notable-common';
import { z } from 'zod';
import { FlowDurationUnits } from '../../../duration/duration.js';
import { FlowTagNameSchema } from '../../../flow/tags.js';
import { FlowProducedType } from '../../../flowRun/producedType.js';
import { FlowRpaJobKind } from '../../../rpaJob/rpaJob.js';
import { FlowBlockValueFieldSchema, FlowBlockValueFieldSchemaVariant, } from '../../blockValue/fieldSchema.js';
import { FlowBlockValueTemplateDurationSchema } from '../../blockValue/templateDuration.js';
import { FlowBlockValueUuidSchema } from '../../blockValue/uuidSchema.js';
import { BaseFlowBlockSchema, FlowBlockReturnTypeSchema } from '../block.js';
import { FlowBlockType } from '../type.js';
import { FlowHumanTaskStepSchema } from './taskConfig.js';
import { FLOW_DEFAULT_ENABLED_HUMAN_TASK_TOOLS, FlowHumanTaskToolsSchema } from './toolsConfig.js';
import { FlowHumanTaskWorkContextSchema } from './workContextConfig.js';
export var FlowHumanTaskPresentationMode;
(function (FlowHumanTaskPresentationMode) {
    FlowHumanTaskPresentationMode["Standard"] = "Standard";
    FlowHumanTaskPresentationMode["BrowserExtension"] = "BrowserExtension";
})(FlowHumanTaskPresentationMode || (FlowHumanTaskPresentationMode = {}));
export var FlowHumanTaskType;
(function (FlowHumanTaskType) {
    FlowHumanTaskType["CustomSubtask"] = "Custom";
    FlowHumanTaskType["SubmitPriorAuthorization"] = "SubmitPriorAuthorization";
    FlowHumanTaskType["DynamicConfig"] = "DynamicConfig";
})(FlowHumanTaskType || (FlowHumanTaskType = {}));
export var FlowHumanTaskAutofillStrategy;
(function (FlowHumanTaskAutofillStrategy) {
    FlowHumanTaskAutofillStrategy["Scripted"] = "Scripted";
    FlowHumanTaskAutofillStrategy["AiAssisted"] = "AiAssisted";
})(FlowHumanTaskAutofillStrategy || (FlowHumanTaskAutofillStrategy = {}));
export var FlowAuthorizationStatusDetailsRequirement;
(function (FlowAuthorizationStatusDetailsRequirement) {
    FlowAuthorizationStatusDetailsRequirement["Required"] = "Required";
    FlowAuthorizationStatusDetailsRequirement["Optional"] = "Optional";
})(FlowAuthorizationStatusDetailsRequirement || (FlowAuthorizationStatusDetailsRequirement = {}));
export const FlowHumanTaskSubtaskSchema = z.object({
    id: z.uuid(),
    name: z.string().optional().default(''),
    steps: z.array(FlowHumanTaskStepSchema),
});
const TagsExpressionSchema = FlowBlockValueFieldSchema({
    variant: FlowBlockValueFieldSchemaVariant.OptionalTemplate,
});
/** Array entries may be template placeholders; `FlowTagNameSchema` is applied in `resolveTagsForConfig`. */
const HumanTaskTagsArraySchema = z
    .array(z.union([TagsExpressionSchema, FlowTagNameSchema]))
    .refine((tags) => new Set(tags).size === tags.length, {
    message: 'All items must be unique, no duplicate values allowed',
});
export const FlowHumanTaskTagsConfigSchema = z.union([
    HumanTaskTagsArraySchema,
    TagsExpressionSchema,
]);
export const BaseFlowHumanTaskBlockConfigSchema = z.object({
    // @deprecated Use subtasks instead
    steps: z.array(FlowHumanTaskStepSchema).optional(),
    subtasks: z.array(FlowHumanTaskSubtaskSchema).default([]),
    workContexts: z.array(FlowHumanTaskWorkContextSchema).default([]),
    expirationDuration: FlowBlockValueTemplateDurationSchema().default({
        value: '1',
        units: FlowDurationUnits.Days,
    }),
    externalId: z.string().nullish(),
    categoryId: FlowBlockValueUuidSchema({ isRequired: false }).nullish(),
    presentationMode: z.enum(FlowHumanTaskPresentationMode).optional(),
    autofillStrategy: z.enum(FlowHumanTaskAutofillStrategy).optional(),
    tools: FlowHumanTaskToolsSchema.default({
        enabledTools: FLOW_DEFAULT_ENABLED_HUMAN_TASK_TOOLS,
        config: {},
    }).optional(),
    workflowTransitionRpaJobKind: z.enum(FlowRpaJobKind).optional(),
    tags: FlowHumanTaskTagsConfigSchema.default([]),
});
export const FlowCustomSubtaskBlockConfigSchema = BaseFlowHumanTaskBlockConfigSchema.extend({
    taskType: z.literal(FlowHumanTaskType.CustomSubtask),
});
export const FlowHumanTaskDynamicConfigBlockConfigSchema = BaseFlowHumanTaskBlockConfigSchema.extend({
    taskType: z.literal(FlowHumanTaskType.DynamicConfig),
    dynamicConfig: FlowBlockValueFieldSchema({
        variant: FlowBlockValueFieldSchemaVariant.TemplateAndPlainString,
    }),
});
export const FlowPriorAuthorizationInformationSchema = z.object({
    patientOrderId: z.string().min(1, { message: 'Patient order ID is required' }),
    renderingFacility: z.string().optional(),
    contactInformation: z.string().optional(),
    clinicalDocumentsPacket: z.string().optional(),
    copilotDataContext: z.string().optional(),
    copilotDataContextObject: z.string().optional(),
    modelName: z.enum(LlmModelName).optional(),
    predictionOptions: z
        .object({
        preamble: z.string().optional(),
        outputRequirement: z.string().optional(),
        outro: z.string().optional(),
    })
        .optional(),
});
export const FlowSubmitPriorAuthorizationBlockConfigSchema = BaseFlowHumanTaskBlockConfigSchema.extend({
    taskType: z.literal(FlowHumanTaskType.SubmitPriorAuthorization),
    priorAuthorizationInformation: FlowPriorAuthorizationInformationSchema,
    /** Defaults to `Required` so existing configs without the field require status details. */
    authorizationStatusDetailsRequirement: z
        .enum(FlowAuthorizationStatusDetailsRequirement)
        .default(FlowAuthorizationStatusDetailsRequirement.Required),
    subtasks: z
        .array(FlowHumanTaskSubtaskSchema.extend({ procedureId: z.uuid().optional() }))
        .default([]),
});
export const FlowHumanTaskBlockConfigSchema = z.discriminatedUnion('taskType', [
    FlowCustomSubtaskBlockConfigSchema,
    FlowSubmitPriorAuthorizationBlockConfigSchema,
    FlowHumanTaskDynamicConfigBlockConfigSchema,
]);
export function isFlowSubmitPriorAuthorizationBlockConfig(blockConfig) {
    return blockConfig.taskType === FlowHumanTaskType.SubmitPriorAuthorization;
}
export const FlowHumanTaskBlockSchema = BaseFlowBlockSchema.extend({
    type: z.literal(FlowBlockType.HumanTask),
    config: FlowHumanTaskBlockConfigSchema,
    returnType: FlowBlockReturnTypeSchema(FlowProducedType.HumanTask),
});
export const FlowHumanTaskExternalIdSchema = z.uuid().nullish();
//# sourceMappingURL=config.js.map